import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppNav } from "@/components/AppNav";
import { useEffect, useState } from "react";
import { ArrowLeft, Pencil } from "lucide-react";
import { supabase, API_URL } from "@/lib/supabase";
import { LOCATION_BY_SLUG } from "@/lib/locations";
import { posthog } from "@/lib/posthog";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { AlertForm } from "@/components/AlertModal";
import { loadPending, clearPending } from "@/lib/pending-alert";
import { uuid } from "@/lib/uuid";

export const Route = createFileRoute("/alertas")({
  validateSearch: (raw: Record<string, unknown>) => {
    const out: {
      create?: string;
      model?: string;
      model_label?: string;
      family?: string;
      q?: string;
      min_year?: number;
      max_year?: number;
      min_km?: number;
      max_km?: number;
      min_price?: number;
      max_price?: number;
      location?: string;
    } = {};
    if (raw.create === "1") out.create = "1";
    if (typeof raw.model === "string") out.model = raw.model;
    if (typeof raw.model_label === "string") out.model_label = raw.model_label;
    if (typeof raw.family === "string") out.family = raw.family;
    if (typeof raw.q === "string") out.q = raw.q;
    const toNum = (v: unknown) => {
      const n = typeof v === "string" ? parseInt(v, 10) : typeof v === "number" ? v : NaN;
      return Number.isFinite(n) ? n : undefined;
    };
    out.min_year = toNum(raw.min_year);
    out.max_year = toNum(raw.max_year);
    out.min_km = toNum(raw.min_km);
    out.max_km = toNum(raw.max_km);
    out.min_price = toNum(raw.min_price);
    out.max_price = toNum(raw.max_price);
    if (typeof raw.location === "string") out.location = raw.location;
    return out;
  },
  head: () => ({
    meta: [{ title: "Meus alertas — Busca Blindados" }],
  }),
  component: AlertsPage,
});

type ModelFilter = { slug: string; display_name?: string; keywords?: string };

type Alert = {
  id: string;
  name: string;
  user_email: string;
  min_year: number | null;
  max_year: number | null;
  min_km: number | null;
  max_km: number | null;
  min_price: number | null;
  max_price: number | null;
  model_filters: ModelFilter[] | null;
  location_slugs: string[] | null;
  is_active: boolean;
  notifications_paused: boolean | null;
  first_notified_at: string | null;
  created_at: string;
};

function fmtBR(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function AlertsPage() {
  const { pathname } = useRouterState({ select: (s) => s.location });
  // A child route ($alertId) is active when the path has a segment after
  // /alertas. Strip a trailing slash first so the list URL itself — which is
  // /alertas/ under trailingSlash:'always' — isn't mistaken for a child.
  if (pathname.replace(/\/$/, "") !== "/alertas") return <Outlet />;

  const { session, loading } = useSession();
  const { isPaid, isFree, isExpired, alertLimit, modelFiltersLimit, profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const alertsKey = ["alerts", session?.user?.id] as const;

  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: alertsKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("filters")
        .select("*")
        .eq("is_active", true)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Alert[];
    },
    enabled: !!session,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const searchParams = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(searchParams.create === "1");
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingAlert, setEditingAlert] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const resendVerification = async () => {
    if (!session) return;
    setResendStatus("sending");
    try {
      const res = await fetch(`${API_URL}/api/send-verification-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setResendStatus(res.ok ? "sent" : "error");
    } catch {
      setResendStatus("error");
    }
  };

  useEffect(() => {
    if (!loading && !session) navigate({ to: "/" });
  }, [session, loading, navigate]);

  // Post-login alert claim: when an existing user fills the home form and
  // then logs in, the AlertModal stashes the payload in localStorage (see
  // src/lib/pending-alert.ts). We pick it up here and create the alert
  // the moment the session is ready.
  const [claimedJustNow, setClaimedJustNow] = useState(false);
  useEffect(() => {
    if (!session) return;
    const pending = loadPending(session.user.email);
    if (!pending) return;
    let cancelled = false;
    (async () => {
      const { data, error: insertError } = await supabase
        .from("filters")
        .insert({
          user_id: session.user.id,
          user_email: session.user.email,
          ...pending,
          unsubscribe_token: uuid(),
        })
        .select("id")
        .single();
      clearPending();
      if (cancelled) return;
      if (insertError) {
        // Most likely the user already has this alert or hit a plan limit.
        // Don't surface — they're on /alertas, they'll see what they have.
        posthog.capture("alert_claim_failed", { code: (insertError as { code?: string }).code });
        return;
      }
      if (data?.id) {
        fetch(`${API_URL}/api/trigger-welcome-email`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ filter_id: data.id }),
        }).catch(() => {});
      }
      posthog.capture("alert_claimed_after_login");
      setClaimedJustNow(true);
      queryClient.invalidateQueries({ queryKey: alertsKey });
    })();
    return () => { cancelled = true; };
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = alerts?.length ?? 0;
  const atLimit = (alertLimit > 0 && activeCount >= alertLimit);
  const [showLimitBanner, setShowLimitBanner] = useState(false);


  const togglePause = async (id: string, paused: boolean) => {
    queryClient.setQueryData(alertsKey, (prev: Alert[]) =>
      prev?.map((a) => (a.id === id ? { ...a, notifications_paused: !paused } : a)),
    );
    const { error } = await supabase
      .from("filters")
      .update({ notifications_paused: !paused })
      .eq("id", id);
    if (error) {
      queryClient.setQueryData(alertsKey, (prev: Alert[]) =>
        prev?.map((a) => (a.id === id ? { ...a, notifications_paused: paused } : a)),
      );
      setError("Falha ao atualizar alerta.");
    } else {
      posthog.capture(paused ? "alert_resumed" : "alert_paused");
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Excluir este alerta? Essa ação não pode ser desfeita.")) return;
    const snapshot = queryClient.getQueryData<Alert[]>(alertsKey);
    queryClient.setQueryData(alertsKey, (prev: Alert[]) => prev?.filter((a) => a.id !== id));
    const { error } = await supabase.from("filters").update({ deleted_at: new Date().toISOString() }).eq("id", id);
    if (error) {
      queryClient.setQueryData(alertsKey, snapshot);
      setError("Falha ao excluir alerta.");
    } else {
      posthog.capture("alert_deleted");
    }
  };

  const updateName = async (id: string, name: string) => {
    const prev_name = alerts?.find((a) => a.id === id)?.name ?? "";
    queryClient.setQueryData(alertsKey, (prev: Alert[]) =>
      prev?.map((a) => (a.id === id ? { ...a, name } : a)),
    );
    setEditingName(null);
    const { error } = await supabase.from("filters").update({ name }).eq("id", id);
    if (error) {
      queryClient.setQueryData(alertsKey, (prev: Alert[]) =>
        prev?.map((a) => (a.id === id ? { ...a, name: prev_name } : a)),
      );
      setError("Falha ao atualizar nome.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando sessão...</p>
      </div>
    );
  }

  if (!session || !session.user) return null;

  return (
    <div className="min-h-screen text-foreground">
      <AppNav />

      <main className="mx-auto max-w-6xl px-6 pb-32 pt-10">
        {claimedJustNow && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4">
            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">✓</div>
            <div>
              <p className="text-sm font-medium text-foreground">Alerta criado!</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Aproveitamos o login pra salvar o alerta que você estava configurando.
              </p>
            </div>
            <button
              onClick={() => setClaimedJustNow(false)}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        )}

        {!profileLoading && profile && !profile.email_verified_at && (
          <div className="mb-6 flex items-start justify-between gap-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 px-5 py-4">
            <div>
              <p className="text-sm font-medium text-foreground">Bem-vindo ao Busca Blindados! Confirme seu email para ativar os alertas.</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Enviamos um link para <strong>{session?.user.email}</strong>. Verifique sua caixa de entrada.
              </p>
              <button
                onClick={resendVerification}
                disabled={resendStatus === "sending" || resendStatus === "sent"}
                className="mt-2 text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resendStatus === "sending" && "Enviando..."}
                {resendStatus === "sent" && "Email reenviado ✓"}
                {resendStatus === "error" && "Erro ao enviar — tente novamente"}
                {resendStatus === "idle" && "Reenviar email"}
              </button>
            </div>
          </div>
        )}


        {!profileLoading && !isPaid && !isFree && !isExpired && alerts && alerts.length > 0 && (
          <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4">
            <p className="text-sm font-medium text-foreground">Seu alerta está salvo.</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Escolha um plano para ativá-lo — assim que surgir uma oportunidade no radar, te avisamos.
            </p>
            <Link
              to="/planos"
              className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
            >
              Ver planos →
            </Link>
          </div>
        )}

        {!profileLoading && isExpired && (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-5 py-4">
            <p className="text-sm font-medium text-foreground">
              Seu plano venceu em{" "}
              {new Date(profile!.plan_expires_at!).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
              .
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Renove para reativar seus alertas e não perder nenhuma oportunidade.
            </p>
            <Link
              to="/planos"
              className="mt-3 inline-block text-sm font-medium text-amber-400 hover:underline"
            >
              Renovar →
            </Link>
          </div>
        )}
        <button
          onClick={() => window.history.back()}
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Voltar
        </button>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-foreground sm:text-4xl">Meus alertas</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Gerencie seus filtros e o email que recebe as notificações.
            </p>
            {alertLimit > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {activeCount} / {alertLimit} alertas ativos
                {profile?.plan && (
                  <span className="ml-2 rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-2 py-0.5 text-[10px]">
                    {profile.plan.display_name}
                  </span>
                )}
                {isFree && (
                  <span className="ml-2 rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-2 py-0.5 text-[10px]">
                    Gratuito
                  </span>
                )}
              </p>
            )}
          </div>
          {(isPaid || isFree) && (
            <button
              onClick={() => {
                if (atLimit) { setShowLimitBanner(true); return; }
                setShowLimitBanner(false);
                setCreating((v) => !v);
                setEditingAlert(null);
              }}
              className="btn-primary shrink-0 rounded-full px-4 py-2.5 text-xs font-medium transition"
            >
              {creating ? "Cancelar" : "+ Novo alerta"}
            </button>
          )}
        </div>

        {showLimitBanner && (
          <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-400">
            {isFree ? (
              <>
                O plano gratuito inclui 1 alerta.{" "}
                <Link to="/planos" className="underline hover:text-amber-300">
                  Assine para criar mais alertas.
                </Link>
              </>
            ) : (
              <>
                Você atingiu o limite de {alertLimit} alertas do{" "}
                {profile?.plan?.display_name}.{" "}
                <Link to="/planos" className="underline hover:text-amber-300">
                  Veja o Plano Monitor
                </Link>{" "}
                para mais alertas.
              </>
            )}
          </div>
        )}

        {creating && (isPaid || isFree) && !atLimit && (
          <div className="surface-card mt-8 rounded-3xl px-6 py-8 sm:px-10">
            <h2 className="font-display text-xl text-foreground">Criar novo alerta</h2>
            <p className="mt-1 text-sm text-muted-foreground">Configure os critérios abaixo.</p>
            <div className="mt-6">
              <AlertForm
                session={session}
                modelLimit={modelFiltersLimit}
                presetModel={
                  searchParams.model
                    ? { slug: searchParams.model, label: searchParams.model_label ?? searchParams.model }
                    : undefined
                }
                presetFilters={{
                  min_year: searchParams.min_year,
                  max_year: searchParams.max_year,
                  min_km: searchParams.min_km,
                  max_km: searchParams.max_km,
                  min_price: searchParams.min_price,
                  max_price: searchParams.max_price,
                  location: searchParams.location,
                }}
                onSuccess={() => {
                  setCreating(false);
                  navigate({ to: "/alertas", search: {} });
                  queryClient.invalidateQueries({ queryKey: alertsKey });
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <p className="mt-8 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </p>
        )}

        <div className="mt-8 space-y-4">
          {alertsLoading && !alerts && (
            <div className="surface-card rounded-2xl p-6 text-sm text-muted-foreground">
              Carregando alertas...
            </div>
          )}
          {!alertsLoading && alerts?.length === 0 && (
            <div className="surface-card rounded-2xl p-10 text-center">
              <p className="font-display text-lg text-foreground">Nenhum alerta ainda</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Configure os modelos e filtros que quer monitorar — avisamos por email quando aparecer.
              </p>
              {(isPaid || isFree) && !atLimit && (
                <button
                  onClick={() => {
                    posthog.capture("alertas_empty_cta_clicked");
                    setCreating(true);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="btn-primary mt-6 rounded-full px-6 py-3 text-sm font-medium transition"
                >
                  + Criar meu primeiro alerta
                </button>
              )}
            </div>
          )}
          {alerts?.map((a) => {
            const isEditing = editingAlert === a.id;
            return (
              <div
                key={a.id}
                className={`surface-card rounded-2xl p-6 transition-opacity duration-300 ${
                  !a.is_active || a.notifications_paused ? "opacity-60" : ""
                }`}
              >
                {/* Card body — collapses when editing */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isEditing ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        {editingName === a.id ? (
                          <NameEditor
                            initial={a.name}
                            onSave={(name) => updateName(a.id, name)}
                            onCancel={() => setEditingName(null)}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="font-display text-base text-foreground">{a.name}</p>
                            {isPaid && (
                              <button
                                onClick={() => setEditingName(a.id)}
                                className="text-white/40 hover:text-white transition-colors"
                                aria-label="Renomear alerta"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {(a.model_filters ?? []).length === 0 ? (
                            <span className="text-sm text-muted-foreground">Qualquer modelo</span>
                          ) : (
                            a.model_filters!.map((mf, i) => (
                              <span
                                key={i}
                                className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs text-foreground"
                              >
                                {mf.display_name ?? mf.slug}
                              </span>
                            ))
                          )}
                          {(a.location_slugs ?? []).length > 0 && (
                            <span className="rounded-full border dark:border-white/15 border-black/15 dark:bg-white/[0.04] bg-black/[0.04] px-3 py-1 text-xs text-foreground/80">
                              {a.location_slugs!.map((s) => LOCATION_BY_SLUG[s] ?? s).join(", ")}
                            </span>
                          )}
                          {!a.is_active && (
                            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-400">
                              Confirme seu email
                            </span>
                          )}
                          {a.is_active && a.notifications_paused && (
                            <span className="rounded-full border dark:border-white/15 border-black/15 dark:bg-white/[0.04] bg-black/[0.04] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              Pausado
                            </span>
                          )}
                          {!isPaid && !isFree && (
                            <span className="rounded-full border dark:border-white/15 border-black/15 dark:bg-white/[0.04] bg-black/[0.04] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              {isExpired ? "Plano vencido" : "Aguardando plano"}
                            </span>
                          )}
                        </div>

                        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
                          <Stat label="Ano min." value={a.min_year ? String(a.min_year) : "—"} />
                          <Stat label="Ano máx." value={a.max_year ? String(a.max_year) : "—"} />
                          <Stat label="KM máx." value={a.max_km ? `${fmtBR(a.max_km)} km` : "—"} />
                          <Stat
                            label="Preço min."
                            value={a.min_price ? `R$ ${fmtBR(a.min_price)}` : "—"}
                          />
                          <Stat
                            label="Preço máx."
                            value={a.max_price ? `R$ ${fmtBR(a.max_price)}` : "—"}
                          />
                        </dl>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center gap-2 border-t dark:border-white/5 border-black/5 pt-4">
                      {(isPaid || isFree) && a.is_active && (
                        <button
                          onClick={() => togglePause(a.id, !!a.notifications_paused)}
                          className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-4 py-2 text-xs text-foreground/90 transition dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                        >
                          {a.notifications_paused ? "Retomar" : "Pausar"}
                        </button>
                      )}
                      {(isPaid || isFree) && (
                        <button
                          onClick={() => setEditingAlert(a.id)}
                          className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-4 py-2 text-xs text-foreground/90 transition dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                        >
                          Editar
                        </button>
                      )}
                      {(isPaid || isFree) && (
                        <button
                          onClick={() => remove(a.id)}
                          className="rounded-full border border-red-500/20 bg-red-500/[0.05] px-4 py-2 text-xs text-red-400 transition hover:bg-red-500/10"
                        >
                          Excluir
                        </button>
                      )}
                      <Link
                        to="/alertas/$alertId"
                        params={{ alertId: a.id }}
                        className="btn-primary ml-auto rounded-full px-4 py-2 text-xs font-medium transition"
                      >
                        Ver radar →
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Edit form — expands when editing */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isEditing ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <AlertForm
                      session={session}
                      modelLimit={modelFiltersLimit}
                      initialValues={{
                        id: a.id,
                        name: a.name,
                        first_notified_at: a.first_notified_at,
                        model_filters: a.model_filters,
                        location_slugs: a.location_slugs,
                        min_year: a.min_year,
                        max_year: a.max_year,
                        min_km: a.min_km,
                        max_km: a.max_km,
                        min_price: a.min_price,
                        max_price: a.max_price,
                      }}
                      onSuccess={() => {
                        setEditingAlert(null);
                        queryClient.invalidateQueries({ queryKey: alertsKey });
                      }}
                    />
                    <button
                      onClick={() => setEditingAlert(null)}
                      className="mt-5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancelar edição
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-foreground">{value}</dd>
    </div>
  );
}

function NameEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (val.trim()) onSave(val.trim());
      }}
      className="flex flex-wrap items-center gap-2"
    >
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="flex-1 rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2 text-sm text-foreground outline-none focus:border-primary/60"
        autoFocus
      />
      <button type="submit" className="btn-primary rounded-full px-4 py-2 text-xs font-medium">
        Salvar
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-4 py-2 text-xs text-foreground/80 dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
      >
        Cancelar
      </button>
    </form>
  );
}
